import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CategoryService from "../../../../services/CategoryService";
import LocationInput from "../../Organisation/locationInput";
import { colors } from "../../../config/colors";
import "./CreateEmployee.css";
import UserService from "../../../../services/UserService";

const CreateEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const [isAvailabilityDropdownOpen, setIsAvailabilityDropdownOpen] =
    useState(false);

  // Add draft-related state
  const DRAFT_STORAGE_KEY = "employee_draft_data";
  const [showDraftModal, setShowDraftModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    address: {
      street: "",
      city: ""
    },
    experience: null,
    skills: [],
    gender: "",
    availability: true,
    role: "EMPLOYEE"
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
  const genderDropdownRef = useRef(null);
  const skillDropdownRef = useRef(null);
  const [passwordError, setPasswordError] = useState("");
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [reuseExistingUser, setReuseExistingUser] = useState(false);
  const [existingUserId, setExistingUserId] = useState(null);
  const [showFullForm, setShowFullForm] = useState(false);

  // Add draft functionality methods
  const saveDraftToLocalStorage = () => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
        formData,
        selectedSkills,
        timestamp: new Date().toISOString()
      }));
      toast.success("Draft saved successfully");
      navigate("/employee-list");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };

  const loadDraftFromLocalStorage = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftData) {
        const parsedData = JSON.parse(draftData);
        setFormData(parsedData.formData);
        setSelectedSkills(parsedData.selectedSkills || []);

        // Clear the draft after loading
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        toast.success("Draft loaded successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error loading draft:", error);
      toast.error("Failed to load draft");
      return false;
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  };

  // Add handlers for the modal actions
  const handleLoadDraft = () => {
    loadDraftFromLocalStorage();
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftModal(false);
  };
  // Add validation message state
  const [validationMessage, setValidationMessage] = useState({
    name: '',
    email: '',
    phone: '',
    'address.street': '',
    experience: ''
  });

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      const fetchEmployeeData = async () => {
        setLoading(true);
        try {
          const response = await UserService.getUserById(id);
          if (response.data.status === "success") {
            const userData = response.data.data.user;
            setFormData({
              name: userData.name,
              email: userData.email,
              phone: userData.phone,
              confirmPassword: "********",
              password: "********",
              address: {
                street: userData.address.street,
                city: userData.address.city
              },
              experience: userData.experience,
              skills: userData.skills ? userData.skills.map(skill => skill._id) : [],
              gender: userData.gender,
              availability: userData.availability,
              role: userData.role,
            });
            setSelectedSkills(userData.skills || []);
          }
        } catch (error) {
          console.error("Error fetching employee data:", error);
          toast.error("Failed to fetch employee data");
        } finally {
          setLoading(false);
        }
      };
      fetchEmployeeData();
    } else {
      // Reset form when switching to create mode
      setIsEditMode(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        address: {
          street: "",
          city: ""
        },
        experience: null,
        skills: [],
        gender: "",
        availability: true,
        role: "EMPLOYEE"
      });

      // Only check for drafts in create mode, not edit mode
      const draftExists = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftExists) {
        setShowDraftModal(true);
      }
    }
    fetchCategories();
  }, [id]);

  useEffect(() => {
    if (!id) {
      setIsEditMode(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        address: {
          street: "",
          city: ""
        },
        experience: null,
        skills: [],
        gender: "",
        availability: true,
        role: "EMPLOYEE"
      });
      setSelectedSkills([]);
      // Optionally reset other state as needed
    }
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(event.target)) {
        setIsGenderDropdownOpen(false);
      }

      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target)) {
        setIsSkillDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAllCategories(false);
      if (response.success) {
        setCategories(response.data || []);
      } else {
        toast.error("Failed to fetch skill types");
      }
    } catch (error) {
      console.error("Error fetching skill types:", error);
      toast.error("Failed to fetch skill types");
    }
  };

  const showTemporaryError = (field, message) => {
    setValidationMessage(prev => ({
      ...prev,
      [field]: message
    }));

    // Clear the message after 1 second
    setTimeout(() => {
      setValidationMessage(prev => ({
        ...prev,
        [field]: ''
      }));
    }, 2000);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    if (password.length < minLength) {
      return "Password must be at least 8 characters long.";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character.";
    }
    return "";
  };

  const validateForm = () => {
    // Name validation
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    // Email validation
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    } else if (!/^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/.test(formData.email.trim())) {
      toast.error("Please enter a valid email address");
      return false;
    }

    // Prevent creating if email is already used in current org
    if (!isEditMode && emailCheckResult?.exists && emailCheckResult?.existsInCurrentOrg) {
      toast.error("Employee already exists in this organisation");
      return false;
    }

    // Phone validation
    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return false;
    } else if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Phone number must be 10 digits");
      return false;
    }

    // Skills validation
    if (!formData.skills.length) {
      toast.error("At least one skill is required");
      return false;
    }

    // Experience validation
    if (formData.experience === undefined || formData.experience < 0) {
      toast.error("Experience must be a non-negative number");
      return false;
    }

    // Address validation
    if (!formData.address.street.trim()) {
      toast.error("Street address is required");
      return false;
    }
    if (!formData.address.city.trim()) {
      toast.error("City is required");
      return false;
    }

    // Password validation only for create mode when not reusing existing user
    if (!isEditMode && !reuseExistingUser) {
      const passwordValidationMsg = validatePassword(formData.password);
      if (passwordValidationMsg) {
        toast.error(passwordValidationMsg);
        setPasswordError(passwordValidationMsg);
        return false;
      }
      if (!formData.confirmPassword) {
        toast.error("Please confirm your password");
        return false;
      } else if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }
    }

    return true;
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name === 'name' || name === 'address.street') {
      // Prevent starting with space
      if (value === ' ') {
        showTemporaryError(name, 'Cannot start with space');
        return;
      }
      // Only allow letters, numbers, spaces and basic punctuation
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, '');
      if (sanitizedValue !== value) {
        showTemporaryError(name, 'Special characters not allowed');
      }

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: sanitizedValue,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: sanitizedValue,
        }));
      }
    } else if (name === 'email') {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        showTemporaryError('email', 'Invalid email format');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === 'phone') {
      // Only allow numbers and limit to 10 digits
      const sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
      if (value !== sanitizedValue) {
        showTemporaryError('phone', 'Only numbers allowed (max 10 digits)');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else if (name === 'experience') {
      // Prevent starting with '00'
      if (value === '00') {
        return;
      }

      // Allow only numbers and decimal point
      if (!/^\d*\.?\d*$/.test(value)) {
        showTemporaryError('experience', 'Only numbers and decimal point allowed');
        return;
      }

      // Prevent more than 1 decimal place
      if (value.includes('.') && value.split('.')[1].length > 1) {
        return;
      }

      // Handle empty input
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          experience: ''
        }));
        return;
      }

      const numValue = parseFloat(value);

      // Prevent negative values
      if (numValue < 0) {
        showTemporaryError('experience', 'Experience cannot be negative');
        return;
      }

      // Prevent values over 100
      if (numValue > 100) {
        showTemporaryError('experience', 'Experience cannot be more than 100');
        value = '100';
      }

      setFormData(prev => ({
        ...prev,
        experience: value
      }));
    } else {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
  };

  const handleLocationChange = (location) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        city: location,
      },
    }));
  };

  const handleValidateEmail = async () => {
    if (!formData.email.trim()) {
      toast.error("Email is required before validation");
      return;
    }
    setEmailCheckLoading(true);
    try {
      const response = await UserService.validateEmployeeEmail(formData.email.trim());
      const payload = response.data || {};
      const data = payload.data || {};
      setEmailCheckResult(data);

      // Email already exists in current organisation
      if (!payload.success && data.exists && data.existsInCurrentOrg) {
        setReuseExistingUser(false);
        setExistingUserId(null);
        toast.error(payload.message || "Employee already exists in this organisation.");
        return;
      }

      // Email exists in another organisation - reuse profile
      if (data.exists && !data.existsInCurrentOrg) {
        const suggestion = data.profileSuggestion || {};
        const user = data.user || {};
        setExistingUserId(user.id || user._id || null);
        setReuseExistingUser(true);

        setFormData((prev) => ({
          ...prev,
          name: suggestion.name || prev.name,
          email: user.email || prev.email,
          phone: suggestion.phone || prev.phone,
          address: {
            street: (suggestion.address && suggestion.address.street) || prev.address.street,
            city: (suggestion.address && suggestion.address.city) || prev.address.city,
          },
          experience: suggestion.experience ?? prev.experience,
          gender: suggestion.gender || prev.gender,
          availability: suggestion.availability ?? prev.availability,
          // Keep org-specific fields (skills) as-is
          password: "",
          confirmPassword: "",
          role: "EMPLOYEE",
        }));

        toast.info(
          payload.message ||
          "Employee exists in another organisation. Email and password will remain the same."
        );
        setShowFullForm(true);
        return;
      }

      // Email available
      setReuseExistingUser(false);
      setExistingUserId(null);
      toast.success(payload.message || "Email is available.");
      setShowFullForm(true);
    } catch (error) {
      console.error("Error validating email:", error);
      toast.error(error.response?.data?.message || "Failed to validate email");
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditMode && !showFullForm) {
      toast.error("Please validate email before continuing");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let employeeData = { ...formData };

      // Remove confirmPassword and handle password for edit mode
      delete employeeData.confirmPassword;
      if (isEditMode && employeeData.password === "********") {
        delete employeeData.password;
      }

      employeeData.role = "EMPLOYEE";

      // When reusing an existing employee from another organisation,
      // do not send password – it stays as is on the backend.
      if (!isEditMode && reuseExistingUser) {
        delete employeeData.password;
      }

      if (!isEditMode && reuseExistingUser && existingUserId) {
        employeeData.existingUserId = existingUserId;
      }

      let response;
      if (isEditMode) {
        response = await UserService.updateUser(id, employeeData);
      } else {
        response = await UserService.createUser(employeeData);
      }

      if (response.data.success) {
        toast.success(`Employee ${isEditMode ? "updated" : "created"} successfully`);
        // Clear any draft if we successfully created an employee
        if (!isEditMode) {
          clearDraft();
        }
        navigate("/employee-list");
      } else {
        toast.error(`Failed to ${isEditMode ? "update" : "create"} employee`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} employee:`, error);
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} employee`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/employee-list");
  };

  const handleSkillRemove = (skillId) => {
    setSelectedSkills(prevSkills => prevSkills.filter(skill => skill._id !== skillId));
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(id => id !== skillId)
    }));
  };

  const handleSkillAdd = async (category) => {
    // Check if skill is already selected
    if (selectedSkills.some(skill => skill._id === category._id)) {
      toast.warning("This skill is already selected");
      return;
    }

    setSelectedSkills(prev => [...prev, category]);
    setFormData(prev => ({
      ...prev,
      skills: [...prev.skills, category._id]
    }));
    setIsSkillDropdownOpen(false);
    setCategorySearchTerm("");
  };

  const handleGenderSelect = (gender) => {
    setFormData(prev => ({
      ...prev,
      gender
    }));
    setIsGenderDropdownOpen(false);
  };

  const filteredCategories = categories?.filter(cat => (cat.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase()) && cat.isActive));

  // Update password input handler
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, password: value }));
    setPasswordError(validatePassword(value));
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10">
      {/* Floating Header - Updated to solid white background */}
      <div className="sticky top-0 bg-white border-b border-accent-light z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-accent-light/20 transition-colors"
              >
                <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-primary-headings">
                {isEditMode ? "Edit Employee" : "Create Employee"}
              </h1>
            </div>

            {/* Add top action buttons for draft functionality */}
            {/* {!isEditMode && (
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={saveDraftToLocalStorage}
                  className="px-6 py-2.5 border border-accent-light/30 rounded-lg text-accent-dark hover:bg-accent-light/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light transition-colors duration-200"
                >
                  Save Draft & Close
                </button>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="w-16 h-16 relative">
              <div className="w-16 h-16 rounded-full border-4 border-accent-light"></div>
              <div className="w-16 h-16 rounded-full border-4 border-primary-headings border-t-transparent animate-spin absolute top-0"></div>
            </div>
            <p className="mt-4 text-accent-dark">
              {isEditMode ? "Updating employee..." : "Creating employee..."}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">Personal Information</h2>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name Input */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Name <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                        placeholder="Enter employee name"
                      />
                      {validationMessage.name && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email Input with validation */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Email <span className="text-cta">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          disabled={isEditMode || reuseExistingUser || showFullForm}
                          className={`w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent ${(isEditMode || reuseExistingUser || showFullForm) ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                          placeholder="Enter email address"
                        />
                        {validationMessage.email && (
                          <p className="mt-1 text-sm text-red-500 absolute">
                            {validationMessage.email}
                          </p>
                        )}
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={showFullForm ? () => {
                            setShowFullForm(false);
                            setReuseExistingUser(false);
                            setExistingUserId(null);
                          } : handleValidateEmail}
                          disabled={emailCheckLoading}
                          className="px-3 py-2.5 text-xs sm:text-sm font-medium text-white rounded-lg shrink-0 disabled:opacity-60"
                          style={{ backgroundColor: colors.primary.headingsBody }}
                        >
                          {emailCheckLoading ? "Checking..." : (showFullForm ? "Change" : "Validate")}
                        </button>
                      )}
                    </div>
                    {reuseExistingUser && (
                      <p className="mt-1 text-xs text-amber-600">
                        This employee exists in another organisation. Email and password will remain
                        the same; you can update profile and organisation-specific details.
                      </p>
                    )}
                  </div>

                  {(isEditMode || showFullForm) && (
                    <>
                      {/* Phone Input */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-primary-headings">
                          Phone <span className="text-cta">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Enter phone number"
                          />
                          {validationMessage.phone && (
                            <p className="mt-1 text-sm text-red-500 absolute">
                              {validationMessage.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Gender Dropdown */}
                      <div className="space-y-1" ref={genderDropdownRef}>
                        <label className="block text-sm font-medium text-primary-headings">
                          Gender <span className="text-cta">*</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setIsGenderDropdownOpen(!isGenderDropdownOpen)}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-left flex justify-between items-center"
                          >
                            <span className={formData.gender ? 'text-primary-headings' : 'text-gray-400'}>
                              {formData.gender ? formData.gender.charAt(0) + formData.gender.slice(1).toLowerCase() : 'Select Gender'}
                            </span>
                            <svg
                              className={`w-5 h-5 text-gray-400 transition-transform ${isGenderDropdownOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {/* Dropdown Menu */}
                          {isGenderDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                              <div className="py-1">
                                {['MALE', 'FEMALE', 'OTHER'].map((gender) => (
                                  <button
                                    key={gender}
                                    type="button"
                                    onClick={() => handleGenderSelect(gender)}
                                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${formData.gender === gender ? 'text-primary-headings font-medium bg-accent-light/5' : 'text-gray-900'
                                      }`}
                                  >
                                    {gender.charAt(0) + gender.slice(1).toLowerCase()}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Skill Type Dropdown */}
                      <div className="space-y-1" ref={skillDropdownRef}>
                        <label className="block text-sm font-medium text-primary-headings">
                          Skills <span className="text-cta">*</span>
                        </label>

                        {/* Skills Search Input */}
                        <div className="relative">
                          <input
                            type="text"
                            value={categorySearchTerm}
                            onChange={(e) => setCategorySearchTerm(e.target.value)}
                            onFocus={() => setIsSkillDropdownOpen(true)}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Search and select skills..."
                          />

                          {/* Skills Dropdown */}
                          {isSkillDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                              <div className="py-1">
                                {filteredCategories
                                  .map((category) => (
                                    <button
                                      key={category._id}
                                      type="button"
                                      onClick={() => handleSkillAdd(category)}
                                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${selectedSkills.some(skill => skill._id === category._id)
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-900'
                                        }`}
                                      disabled={selectedSkills.some(skill => skill._id === category._id)}
                                    >
                                      {category.categoryType}
                                    </button>
                                  ))}
                                {filteredCategories.length === 0 && (
                                  <div className="px-4 py-2 text-gray-500 text-center">
                                    No skills found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>


                        {/* Selected Skills Display */}
                        {selectedSkills.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {selectedSkills.map((skill) => (
                              <div
                                key={skill._id}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-primary-light/10 text-primary-headings rounded-full"
                              >
                                <span className="text-sm">{skill.categoryType}</span>
                                <button
                                  type="button"
                                  onClick={() => handleSkillRemove(skill._id)}
                                  className="p-1 hover:bg-primary-light/20 rounded-full"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {(isEditMode || showFullForm) && (
              <>
                {/* Address Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
                  <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h2 className="text-lg font-semibold text-primary-headings">Address</h2>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Street Input */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-primary-headings">
                          Street <span className="text-cta">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="address.street"
                            value={formData.address.street}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Enter street address"
                          />
                          {validationMessage['address.street'] && (
                            <p className="mt-1 text-sm text-red-500 absolute">
                              {validationMessage['address.street']}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* City Input */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-primary-headings">
                          City <span className="text-cta">*</span>
                        </label>
                        <LocationInput
                          setUserLocation={handleLocationChange}
                          setLocationSelected={setLocationSelected}
                          location={formData.address.city}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Password Card (for create mode, not when reusing existing user) */}
                {!isEditMode && !reuseExistingUser && (
                  <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
                    <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <h2 className="text-lg font-semibold text-primary-headings">Password</h2>
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Password Input */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-primary-headings">
                            Password <span className="text-cta">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handlePasswordChange}
                              className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                              placeholder="Enter password"
                            />
                            {passwordError && (
                              <p className="mt-1 text-xs text-red-500">{passwordError}</p>
                            )}
                          </div>
                        </div>

                        {/* Confirm Password Input */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-primary-headings">
                            Confirm Password <span className="text-cta">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                              placeholder="Confirm password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showConfirmPassword ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Availability Input */}
                <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
                  <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h2 className="text-lg font-semibold text-primary-headings">Availability</h2>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Availability Input */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-primary-headings">
                          Availability <span className="text-cta">*</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setIsAvailabilityDropdownOpen(!isAvailabilityDropdownOpen)}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                          >
                            {formData.availability ? "Available" : "Not Available"}
                          </button>
                          {isAvailabilityDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, availability: true }));
                                  setIsAvailabilityDropdownOpen(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                              >
                                Available
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, availability: false }));
                                  setIsAvailabilityDropdownOpen(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                              >
                                Not Available
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Experience Input */}
                <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
                  <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h2 className="text-lg font-semibold text-primary-headings">Experience</h2>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Experience Input */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-primary-headings">
                          Experience (years) <span className="text-cta">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="experience"
                            value={formData.experience || ''}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            placeholder="Enter years of experience"
                          />
                          {validationMessage.experience && (
                            <p className="mt-1 text-sm text-red-500 absolute">
                              {validationMessage.experience}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 border-t border-accent-light/20 pt-6">
                  <div className="flex justify-end items-center">
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200"
                    >
                      {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Employee" : "Create Employee")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>
        )}
      </div>

      {/* Draft Modal */}
      {showDraftModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-light/20">
                    <svg className="w-6 h-6 text-primary-headings" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium leading-6 text-primary-headings">
                    Draft Found
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-accent-dark">
                      A draft employee was found. Would you like to load it?
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="px-4 py-2 text-sm font-medium text-accent-dark bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light"
                >
                  No, Discard
                </button>
                <button
                  type="button"
                  onClick={handleLoadDraft}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-headings hover:bg-primary-headings/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                >
                  Yes, Load Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateEmployee;
